/**
 * @fileoverview Invitation-acceptance API calls.
 * @description The two calls the /invitation/accept page needs: whether the emailed link is still
 * usable (GET /identity/invitations/{token}/validate) and redeeming it with the account's first
 * password (POST /identity/invitations/accept). Validate deliberately says nothing else — not the
 * address, not the account's status — and answers every unusable link (unknown, expired, already
 * used, revoked) with the same 400 / appCode 1040, so the page can't tell those apart either.
 * Accepting doesn't sign the user in: it registers the account and emails an activation code,
 * returned here as a challenge that activation.service.ts verifies.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

export interface InvitationValidationResponse {
  valid: true
  nextStep: 'SET_PASSWORD'
}

export interface AcceptInvitationRequest {
  token: string
  newPassword: string
  confirmPassword: string
}

/** The activation challenge an accepted invitation opens (AcceptInvitationResponseDto). */
export interface AcceptInvitationResponse {
  invitationAccepted: true
  nextStep: 'VERIFY_ACTIVATION_MFA'
  challengeId: string
  channel: 'EMAIL'
  maskedDestination: string
  expiresInSeconds: number
  resendAvailableInSeconds: number
}

/**
 * @description Checks whether an invitation link is still usable, without consuming it.
 * @param {string} token - The token from the emailed invitation link.
 * @returns {Promise<InvitationValidationResponse>} Confirmation the link can be redeemed.
 */
export async function validateInvitation(token: string): Promise<InvitationValidationResponse> {
  const { data } = await httpClient.get<InvitationValidationResponse>(
    `/identity/invitations/${encodeURIComponent(token)}/validate`,
  )
  return data
}

/**
 * @description Redeems an invitation: sets the account's first password and emails an activation
 * code.
 * @param {AcceptInvitationRequest} payload - The token and the chosen password, twice.
 * @returns {Promise<AcceptInvitationResponse>} The activation challenge to verify next.
 */
export async function acceptInvitation(
  payload: AcceptInvitationRequest,
): Promise<AcceptInvitationResponse> {
  const { data } = await httpClient.post<AcceptInvitationResponse>(
    '/identity/invitations/accept',
    payload,
  )
  return data
}
